import * as d3 from 'd3';

/**
 * Axis tick formatter that never falls off the bottom of the SI scale.
 *
 * d3's SI format ('-2~s') gives sub-unit values a metric prefix, so a tick at 0.5 renders
 * as "500m" and one at 0.002 as "2m". An axis drops below 1 whenever d3 picks a fractional
 * tick step - a short domain, or a brush/slider zoom into one - and those milli labels then
 * read as nonsense beside the whole numbers around them.
 *
 * Formats with the given spec from 1 upwards and swaps to d3's 'r' type below it - the
 * plain-decimal twin of 's', which counts precision in significant digits just as 's' does,
 * so '.0s' still resolves 0.25 and 0.05 instead of rounding both to "0". Any prefix, such as
 * the '$' in '$.2~s', survives the swap. A spec that is not SI is handed back untouched - it
 * never had the problem.
 *
 * Only tick labels built by hand need this. An axis that passes its spec to d3 as
 * `.ticks(count, spec)` already goes through scale.tickFormat, which locks a single SI
 * prefix to the domain and so cannot emit a milli label.
 */
export function axisTickFormat(spec: string): (value: any) => string {
  const format = d3.format(spec);
  if (!spec || !spec.endsWith('s')) {
    return format;
  }
  const base = spec.slice(0, -1);
  const decimal = d3.format(base.includes('~') ? base + 'r' : base + '~r');
  return (value: any) => Math.abs(+value) < 1 ? decimal(value) : format(value);
}
